import { Link } from "react-router-dom";
import { Globe, Mail, MapPin } from "lucide-react";
import { Reveal } from "./kit";
import { OCEAN_PATCH_URL, OCEAN_POSTER_URL, OCEAN_COMPLIANCE } from "../../data/oceanContent";

export default function OceanClosing() {
  const goTo = (href) => (e) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <section data-testid="ocean-compliance-section" className="relative py-20 lg:py-28 bg-oabyss/30">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-5">
                <div className="font-mono text-[11px] tracking-[0.3em] text-oreef mb-4">STATUS &amp; SCIENTIFIC CREDIBILITY</div>
                <h2 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-4xl text-oseaglass leading-tight">Proposed work, honestly stated</h2>
                <div className="mt-8 border border-oreef/15 overflow-hidden clip-corner max-w-xs">
                  <img src={OCEAN_POSTER_URL} alt="Arjuna Indian Ocean Expedition recruitment poster" className="w-full object-cover" data-testid="ocean-poster-asset" />
                </div>
              </div>
              <div className="lg:col-span-7">
                <ul className="space-y-3" data-testid="ocean-compliance-list">
                  {OCEAN_COMPLIANCE.map((c) => (
                    <li key={c} className="text-sm text-oseaglass/75 flex gap-3 leading-relaxed border-b border-oslate/10 pb-3"><span className="text-ocoral shrink-0 mt-0.5">▸</span>{c}</li>
                  ))}
                </ul>
                <p className="mt-6 font-mono text-[10px] tracking-[0.2em] text-oslate leading-relaxed">
                  WE DISTINGUISH PROPOSED FROM COMPLETED · PLANNED FROM CONFIRMED · RECREATIONAL FROM AUTHORISED SCIENTIFIC
                  DIVING · SATELLITE INDICATORS FROM DIRECT MEASUREMENTS.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="contact" data-testid="ocean-closing-banner" className="relative py-28 lg:py-40 overflow-hidden">
        <div className="absolute inset-0 ocean-grid opacity-40 pointer-events-none" />
        <div className="absolute inset-0 ocean-vignette pointer-events-none" />
        <div className="relative max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12 text-center">
          <Reveal>
            <div className="font-mono text-[11px] tracking-[0.35em] text-oreef mb-8">ARJUNA INDIAN OCEAN EXPEDITION · PROPOSED</div>
            <h2 className="font-display font-extrabold uppercase tracking-tight leading-[0.9] text-[11vw] sm:text-[8vw] lg:text-7xl xl:text-8xl text-oseaglass">
              The ocean <span className="text-reef-stroke">connects us all.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-10 max-w-3xl mx-auto text-base sm:text-lg text-oseaglass/70 leading-relaxed">
              From coral reefs and island lagoons to coastal communities and the open sea, understanding India's marine
              environments requires better observations, better data and better questions.
            </p>
            <p className="mt-4 max-w-3xl mx-auto text-base sm:text-lg text-oseaglass/70 leading-relaxed">
              Arjuna Indian Ocean Expedition is a proposed step towards connecting underwater exploration with Earth intelligence.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
              <a href="#join" onClick={goTo("#join")} data-testid="ocean-closing-cta-join" className="bg-ocoral text-onavy font-mono text-xs font-semibold uppercase tracking-[0.2em] px-8 py-4 clip-corner hover:bg-oseaglass transition-colors duration-300">
                Join the Mission
              </a>
              <a href="#partners" onClick={goTo("#partners")} data-testid="ocean-closing-cta-partner" className="border border-oreef/40 text-oreef font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 clip-corner-tl hover:border-oreef hover:bg-oreef/10 transition-colors duration-300">
                Partner With Arjuna
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto text-left" data-testid="ocean-contact-details">
              <div className="border border-oreef/15 bg-oabyss/50 p-6">
                <MapPin size={18} className="text-oreef mb-3" strokeWidth={1.5} />
                <div className="font-mono text-[10px] tracking-[0.25em] text-oslate mb-1.5">HEADQUARTERS</div>
                <p className="text-sm text-oseaglass/80">Arjuna Space AI India<br />Pune, Maharashtra, India</p>
              </div>
              <div className="border border-oreef/15 bg-oabyss/50 p-6">
                <Globe size={18} className="text-oreef mb-3" strokeWidth={1.5} />
                <div className="font-mono text-[10px] tracking-[0.25em] text-oslate mb-1.5">WEB</div>
                <a href="https://www.arjunaspace.in" target="_blank" rel="noopener noreferrer" className="text-sm text-oreef hover:text-oseaglass transition-colors" data-testid="ocean-contact-website-link">www.arjunaspace.in</a>
              </div>
              <div className="border border-oreef/15 bg-oabyss/50 p-6">
                <Mail size={18} className="text-oreef mb-3" strokeWidth={1.5} />
                <div className="font-mono text-[10px] tracking-[0.25em] text-oslate mb-1.5">ENQUIRIES</div>
                <p className="text-sm text-oseaglass/80">Via the <a href="#partners" onClick={goTo("#partners")} className="text-oreef hover:text-oseaglass transition-colors">partnership</a> and <a href="#join" onClick={goTo("#join")} className="text-oreef hover:text-oseaglass transition-colors">interest</a> forms.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <footer className="relative border-t border-oreef/10 bg-onavy" data-testid="ocean-site-footer">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12 py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5 flex items-start gap-4">
              <img src={OCEAN_PATCH_URL} alt="Arjuna Indian Ocean Expedition mission patch" className="w-14 h-14 rounded-full ring-1 ring-ogold/40 shrink-0 object-cover" />
              <div>
                <div className="font-display font-bold text-2xl text-oseaglass tracking-wide">ARJUNA SPACE AI INDIA</div>
                <div className="font-mono text-[10px] tracking-[0.3em] text-ogold mt-1.5">SAME OCEAN. BIGGER QUESTIONS.</div>
                <a href="https://www.arjunaspace.in" target="_blank" rel="noopener noreferrer" className="font-mono text-[11px] text-oreef hover:text-oseaglass transition-colors mt-2 inline-block" data-testid="ocean-footer-website-link">https://www.arjunaspace.in</a>
              </div>
            </div>
            <div className="lg:col-span-4" data-testid="ocean-footer-environmental">
              <div className="font-mono text-[10px] tracking-[0.25em] text-oslate mb-3">ENVIRONMENTAL RESPONSIBILITY</div>
              <p className="text-xs text-oseaglass/55 leading-relaxed">
                Any proposed field activity will follow applicable diving rules, marine protected-area restrictions,
                wildlife-protection regulations, vessel requirements and all required permissions. No field activity will
                occur without the necessary authorisations.
              </p>
            </div>
            <div className="lg:col-span-3" data-testid="ocean-footer-telemetry">
              <div className="font-mono text-[10px] tracking-[0.25em] text-oslate mb-3">SCOPE</div>
              <div className="font-mono text-[11px] text-oseaglass/60 space-y-1.5 tracking-wider">
                <div>ARABIAN SEA · BAY OF BENGAL</div>
                <div>LAKSHADWEEP · ANDAMAN &amp; NICOBAR</div>
                <div>BASE — 18°31′N 073°51′E</div>
                <div className="text-oslate">STATUS — DEVELOPMENT PHASE</div>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-oreef/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="ocean-footer-links">
            <p className="font-mono text-[10px] tracking-[0.2em] text-oslate">© 2026 ARJUNA SPACE AI INDIA. A PROPOSED MISSION — NOTHING HERE IS A CONFIRMED RESULT, PARTNERSHIP OR AUTHORISATION.</p>
            <div className="flex gap-6 font-mono text-[10px] tracking-[0.2em]">
              <a href="#partners" onClick={goTo("#partners")} className="text-oslate hover:text-oreef transition-colors">PARTNER</a>
              <a href="#join" onClick={goTo("#join")} className="text-oslate hover:text-oreef transition-colors">JOIN</a>
              <Link to="/ocean/updates" className="text-oslate hover:text-oreef transition-colors">JOURNAL</Link>
              <Link to="/" className="text-oslate hover:text-polar transition-colors">ANTARCTICA</Link>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
